'use client';

import { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Link from 'next/link';
import { T, useLocale } from '@/i18n/LocaleProvider';
import { Button } from '@/components';
import { analytics } from '@/utils/analytics';
import { GTM_EVENTS, GTM_SOURCES } from '@/constants/gtm';
import { buildNewsletterSubscription, sendNewsletterSubscription } from '@/libs/leads';
import {
  LISTAS,
  LISTA_TODAS,
  PAISES_NEWSLETTER,
  PAIS_POR_DEFECTO,
  alternarLista,
  listasElegidas,
  necesitaPais,
} from '@/libs/content/newsletter';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const BORDE = '#e2e5e3'; // grey.200: MUI no resuelve los tokens grey dentro de algunos bordes responsivos
const TINTA = '#0a0f0e';

const Check = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
    <path d="M3 8.5l3.2 3.2L13 4.8" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const linkSx = { color: 'text.accent', textDecoration: 'underline' };

/**
 * Formulario de suscripción: chips con las listas, campo con el botón adentro y consentimiento. Sin marco ni
 * título: los pone quien lo usa (la tarjeta de blog y actualidad, la página /newsletter).
 * `source` (GTM) y `origin` (campo «origin» del CRM, ≤60) dicen desde dónde se suscribió la persona.
 * Envía al CRM (/api/newsletter/subscribe, INI-1081), que manda el correo para confirmar la suscripción.
 */
export default function NewsletterForm({
  source = GTM_SOURCES.BLOG_NEWSLETTER,
  origin = 'Blog · Newsletter',
  listas = [LISTA_TODAS],
}) {
  const { locale } = useLocale();
  const [seleccion, setSeleccion] = useState(listas);
  const [pais, setPais] = useState(PAIS_POR_DEFECTO);
  const [email, setEmail] = useState('');
  const conPais = necesitaPais(seleccion);
  const [consent, setConsent] = useState(false);
  const [botcheck, setBotcheck] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (botcheck) return; // honeypot
    if (!EMAIL_RE.test(email.trim())) {
      setError('Ingresa un email válido.');
      return;
    }
    if (!consent) {
      setError('Necesitamos tu consentimiento para suscribirte.');
      return;
    }
    setError('');
    setStatus('loading');

    const ok = await sendNewsletterSubscription(
      buildNewsletterSubscription({ email, lists: listasElegidas(seleccion, pais), locale, origin, botcheck }),
    );
    if (ok) {
      analytics.custom(GTM_EVENTS.NEWSLETTER_SIGNUP, { source });
      setStatus('success');
      setEmail('');
      setConsent(false);
    } else {
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <Box
        role="status"
        sx={{
          px: '24px',
          py: '18px',
          bgcolor: 'common.white',
          border: `1px solid ${BORDE}`,
          borderRadius: '999px',
          color: 'primary.darker',
          fontSize: '15px',
          lineHeight: 1.5,
          textAlign: 'center',
        }}
      >
        <T>Te enviamos un correo para confirmar tu suscripción. Ábrelo y confirma para empezar a recibirlo.</T>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      {/* Honeypot anti-spam */}
      <Box
        component="input"
        type="text"
        name="botcheck"
        value={botcheck}
        onChange={(e) => setBotcheck(e.target.value)}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        sx={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
      />

      {/* Qué listas: todas a la vista, en las filas que hagan falta (dos en escritorio) */}
      <Box
        role="group"
        aria-label="Qué quieres recibir"
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
          mb: '16px',
        }}
      >
        {LISTAS.map((l) => {
          const activa = seleccion.includes(l.id);
          return (
            <Box
              key={l.id}
              component="button"
              type="button"
              aria-pressed={activa}
              onClick={() => setSeleccion((s) => alternarLista(s, l.id))}
              sx={{
                flexShrink: 0,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                height: '40px',
                px: '18px',
                border: `1px solid ${activa ? TINTA : BORDE}`,
                borderRadius: '999px',
                bgcolor: activa ? TINTA : 'common.white',
                color: activa ? 'common.white' : 'text.primary',
                fontFamily: 'inherit',
                fontSize: '15px',
                fontWeight: 500,
                lineHeight: 1,
                whiteSpace: 'nowrap',
                cursor: 'pointer',
                transition: 'border-color 0.15s, background-color 0.15s',
                '&:hover': { borderColor: activa ? TINTA : 'grey.500' },
                '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: '2px' },
              }}
            >
              {activa && <Check />}
              <T>{l.label}</T>
            </Box>
          );
        })}
        {/* El país, aparte: solo cuando lo elegido incluye rankings («Todas» los incluye) */}
        {conPais && (
          <Box
            component="select"
            value={pais}
            onChange={(e) => {
              setPais(e.target.value);
              if (error) setError('');
            }}
            aria-label="Tu país"
            sx={{
              height: '40px',
              pl: '18px',
              pr: '40px',
              border: `1px solid ${pais ? TINTA : BORDE}`,
              borderRadius: '999px',
              bgcolor: 'common.white',
              color: pais ? 'text.primary' : 'grey.700',
              fontFamily: 'inherit',
              fontSize: '15px',
              fontWeight: 500,
              cursor: 'pointer',
              appearance: 'none',
              backgroundImage:
                "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 16 16'%3E%3Cpath d='M4 6l4 4 4-4' fill='none' stroke='%230a0f0e' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")",
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 16px center',
              '&:hover': { borderColor: pais ? TINTA : 'grey.500' },
              '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: '2px' },
            }}
          >
            {PAISES_NEWSLETTER.map((p) => (
              <option key={p.id} value={p.id}>
                {p.nombre}
              </option>
            ))}
          </Box>
        )}
      </Box>

      {/* Campo con el botón adentro */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          pl: '22px',
          pr: '8px',
          py: '8px',
          bgcolor: 'common.white',
          border: '1px solid',
          borderColor: error ? 'error.main' : BORDE,
          borderRadius: '999px',
          '&:focus-within': { borderColor: 'primary.main', boxShadow: '0 0 0 3px rgba(0,159,139,0.12)' },
        }}
      >
        <Box
          component="input"
          type="email"
          name="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (error) setError('');
          }}
          placeholder="tu@empresa.com"
          aria-label="Email para newsletter"
          aria-invalid={!!error}
          autoComplete="email"
          required
          sx={{
            flex: 1,
            minWidth: 0,
            py: '10px',
            border: 'none',
            bgcolor: 'transparent',
            fontFamily: 'inherit',
            fontSize: '16px',
            color: 'text.primary',
            outline: 'none',
            '&::placeholder': { color: 'text.muted', opacity: 1 },
          }}
        />
        <Button type="submit" variant="contained" disabled={status === 'loading'} sx={{ borderRadius: '999px', flexShrink: 0 }}>
          {status === 'loading' ? <T>Enviando…</T> : <T>Suscribirse</T>}
        </Button>
      </Box>

      {/* Consentimiento (Ley 21.719) */}
      <Box
        component="label"
        sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', gap: '10px', mt: '16px', cursor: 'pointer' }}
      >
        <Box
          component="input"
          type="checkbox"
          checked={consent}
          onChange={(e) => {
            setConsent(e.target.checked);
            if (error) setError('');
          }}
          sx={{ accentColor: '#009F8B', cursor: 'pointer', width: '18px', height: '18px', mt: '1px', flexShrink: 0 }}
        />
        <Typography component="span" sx={{ fontSize: '13px', lineHeight: 1.5, color: 'grey.600' }}>
          <T>Acepto los</T>{' '}
          <Box component={Link} href="/terminos" sx={linkSx}>
            <T>Términos</T>
          </Box>{' '}
          <T>y la</T>{' '}
          <Box component={Link} href="/privacidad" sx={linkSx}>
            <T>Política de privacidad</T>
          </Box>
          . <T>Cancela tu suscripción en cualquier momento.</T>
        </Typography>
      </Box>

      {(error || status === 'error') && (
        <Typography role="alert" sx={{ fontSize: '13px', lineHeight: 1.5, color: 'error.main', mt: '10px', textAlign: 'center' }}>
          {error || 'No pudimos completar la suscripción. Reintenta en un momento.'}
        </Typography>
      )}
    </Box>
  );
}
